import { printerStore, type PrinterEvent } from '@goshippo/print';

import { type PrintEventProperties } from '~/modules/devices/types/deviceEventCatalog.type';
import { publish } from '~/modules/devices/utils/deviceEventsPublisher.util';

import { PRINT_LIFECYCLE_TIMEOUT_MS } from '../constants/printStatus.const';
import { classifyPrintStatus } from './printStatus.util';

type PendingPrint = {
  baseContext: PrintEventProperties;
  printerName: string;
  submittedAt: number;
  timeoutHandle: ReturnType<typeof setTimeout>;
};

const pending = new Map<string, PendingPrint>();

export const registerPendingPrint = (entry: {
  baseContext: PrintEventProperties;
  printerName: string;
}): void => {
  const existing = pending.get(entry.printerName);
  if (existing) {
    clearTimeout(existing.timeoutHandle);
    // eslint-disable-next-line no-console
    console.warn(
      `[device-events] replacing pending print for printer "${entry.printerName}" — earlier print's terminal event will be ignored`,
    );
  }

  const submittedAt = Date.now();
  const timeoutHandle = setTimeout(() => {
    const stuck = pending.get(entry.printerName);
    if (!stuck) return;
    pending.delete(entry.printerName);
    publish('print', 'failed', {
      ...stuck.baseContext,
      error_code: 'TIMEOUT',
      error_message: 'Print did not report a terminal status before timeout',
    });
  }, PRINT_LIFECYCLE_TIMEOUT_MS);

  pending.set(entry.printerName, {
    baseContext: entry.baseContext,
    printerName: entry.printerName,
    submittedAt,
    timeoutHandle,
  });
};

export const resolvePendingFromStatus = (
  printerName: string,
  statusText: string,
  statusCode: null | string,
): void => {
  const entry = pending.get(printerName);
  if (!entry) return;

  const outcome = classifyPrintStatus(statusText);
  if (outcome === 'in_flight') return;

  pending.delete(entry.printerName);
  clearTimeout(entry.timeoutHandle);

  const extras: Partial<PrintEventProperties> = {
    print_job_status_code: statusCode ?? statusText,
    status: statusText,
  };
  if (outcome === 'failed') {
    extras.error_code = statusText;
    extras.error_message = `Print failed with status ${statusText}`;
  }

  publish('print', outcome, { ...entry.baseContext, ...extras });
};

export const clearAllPendingPrints = (): void => {
  for (const entry of pending.values()) clearTimeout(entry.timeoutHandle);
  pending.clear();
};

export const getPendingPrintsForTesting = (): ReadonlyMap<string, PendingPrint> => pending;

/**
 * Processes new printer status events and resolves pending `device.print.*` lifecycles.
 * Returns the next index to read from on the next notification.
 */
export const processPrinterStatusEvents = (
  events: readonly PrinterEvent[],
  fromIndex: number,
): number => {
  let nextIndex = fromIndex;

  if (nextIndex > events.length) nextIndex = 0;

  for (let index = nextIndex; index < events.length; index += 1) {
    const event = events[index];
    if (event.kind !== 'ACTION' && event.kind !== 'STATUS') continue;
    const statusText = String(event.statusText);
    const statusCode = event.statusCode === null ? null : String(event.statusCode);
    resolvePendingFromStatus(event.printerName, statusText, statusCode);
  }

  return events.length;
};

let lastProcessedIndex = 0;
let relayInitialized = false;

/**
 * Subscribes to shippo-print `printerStore` and relays terminal STATUS/ACTION callbacks
 * into device-events via {@link resolvePendingFromStatus}. Idempotent — safe to call once
 * from app bootstrap (alongside {@link setupShippoPrint}).
 */
export const setupPrintLifecycleRelay = (): void => {
  if (relayInitialized) return;
  relayInitialized = true;

  printerStore.subscribe(() => {
    const { events } = printerStore.getSnapshot();
    lastProcessedIndex = processPrinterStatusEvents(events, lastProcessedIndex);
  });
};

/** @internal Resets relay cursor and init guard — for tests only. */
export const resetPrintLifecycleRelayForTesting = (): void => {
  lastProcessedIndex = 0;
  relayInitialized = false;
};
